import { Alert, Box, Button, Typography } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { useEffect, useState } from 'react';
import {
  ArrowTab,
  CustomTabPanel,
  IconLibrary,
  ArrowTabGroup,
  StackablePageButtons,
  PageSection,
} from '@product-framework/index';
import {
  SkeletonListBoxSelector,
  SnackbarApiErrorMessage,
  useDataProvider,
  useEntityPermission,
  useSnackbar,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import IndustriesServiced from './IndustriesServiced';
import ProductLinesSelector from './ProductLinesSelector';
import IndustriesServicedSelector from './IndustriesServicedSelector';
import BrandsSelector from './BrandsSelector';

function AutoSaveMessage({ permission = 'WRITE' }) {
  if (permission !== 'WRITE') {
    return null;
  }
  return (
    <Grid2 display="flex" justifyContent="center">
      <Box width="255px">
        <Alert severity="info">
          <FormattedMessage
            id="CHANGES_SAVED_AUTOMATICALLY"
            defaultMessage="Changes saved automatically"
          />
        </Alert>
      </Box>
    </Grid2>
  );
}

function SkillsSetup({
  permissionName,
  config,
  onChange = () => undefined,
  pageSectionLabel,
}) {
  const snackbar = useSnackbar();
  const { getPermission } = useEntityPermission();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get } = useDataProvider(provider);

  const [view, setView] = useState(null);
  const [stackablePageButtons, setStackablePageButtons] = useState([]);
  const [hasIndustriesServiced, setHasIndustriesServiced] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState({});
  const [permissions, setPermissions] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  const configureIndustriesButton = {
    label: (
      <FormattedMessage
        id="CONFIGURE_INDUSTRIES_SERVICED"
        defaultMessage="Configure Industries Serviced"
      />
    ),
    id: 'CONFIGURE_INDUSTRIES_SERVICED',
    Icon: IconLibrary.Industries,
    variant: 'outlined',
    onClick: () => {
      setView(0);
    },
  };

  const configureProductLineAndBrandsButton = {
    label: (
      <FormattedMessage
        id="CONFIGURE_PRODUCT_LINES_AND_BRANDS"
        defaultMessage="Configure Product Lines and Brands"
      />
    ),
    id: 'CONFIGURE_PRODUCT_LINES_AND_BRANDS',
    Icon: IconLibrary.ServiceAdministratorProductLines,
    variant: 'outlined',
    onClick: () => {
      setView(1);
    },
  };

  const fetchCurrentIndustriesServiced = async () => {
    if (!config?.api?.industriesServiced?.fetch) {
      return null;
    }
    const response = await get(config?.api?.industriesServiced?.fetch);
    if (response.ok) {
      return response?.data?.data || [];
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const updateHasIndustriesServiced = async () => {
    const result = await fetchCurrentIndustriesServiced();
    const hasResult = Boolean(result?.length);
    setHasIndustriesServiced(hasResult);
    return hasResult;
  };

  const initialize = async () => {
    setLoading(true);
    const permissionsFound = await getPermission({
      name: permissionName,
    });
    setPermissions(permissionsFound);
    const hasResult = await updateHasIndustriesServiced();
    if (hasResult) {
      setView(1);
    } else {
      setView(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (view === 0) {
      setStackablePageButtons([configureProductLineAndBrandsButton]);
    } else if (view === 1) {
      setStackablePageButtons([configureIndustriesButton]);
    }
  }, [view]);

  useEffect(() => {
    initialize();
  }, []);

  return (
    <PageSection
      HeaderContent={
        <StackablePageButtons
          backButton={false}
          buttons={stackablePageButtons}
        />
      }
      headerProps={{ sx: { mb: 4 } }}
      label={pageSectionLabel}>
      {loading && <SkeletonListBoxSelector height="400px" />}

      {view === 0 && (
        <Grid2 mb={5} mt={5} textAlign="center">
          <IndustriesServicedSelector
            permissions={permissions}
            onChange={async () => {
              await updateHasIndustriesServiced();
              onChange();
            }}
            config={config}
          />
          <AutoSaveMessage permission={permissions.industryId} />
        </Grid2>
      )}
      {view === 1 && !hasIndustriesServiced && (
        <Grid2 mb={5} mt={5} textAlign="center">
          <Box
            sx={{
              width: '40%',
              margin: '0 auto',
              height: '300px',
            }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 200 }}>
              <FormattedMessage
                id="YOU_HAVE_NOT_ADDED_ANY_INDUSTRIES_SERVICED_YET"
                defaultMessage="You have not added any Industries Serviced yet."
              />
            </Typography>
            <Button
              onClick={() => setView(0)}
              startIcon={<IconLibrary.Industries />}
              variant="contained">
              <FormattedMessage
                id="ADD_INDUSTRIES_SERVICED"
                defaultMessage="Add Industries Serviced"
              />
            </Button>
          </Box>
        </Grid2>
      )}
      {view === 1 && hasIndustriesServiced && (
        <Grid2 container spacing={2}>
          <Grid2 xs={12} md={3}>
            <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
              <FormattedMessage
                id="INDUSTRIES_SERVICED"
                defaultMessage="Industries Serviced"
              />
            </Typography>

            <IndustriesServiced
              config={config}
              onChange={data => setSelectedIndustry(data)}
            />
          </Grid2>
          <Grid2 xs={12} md={9}>
            <Box>
              <ArrowTabGroup
                value={tabValue}
                onChange={(event, newValue) => setTabValue(newValue)}>
                <ArrowTab
                  arrowHeadArea={89}
                  maxTabs={2}
                  label={
                    <FormattedMessage
                      id="PRODUCT_LINES"
                      defaultMessage="Product Lines"
                    />
                  }
                />
                <ArrowTab
                  arrowBackArea={5}
                  maxTabs={2}
                  label={
                    <FormattedMessage id="BRANDS" defaultMessage="Brands" />
                  }
                />
              </ArrowTabGroup>
            </Box>
            <Grid2 xs={12}>
              <CustomTabPanel value={tabValue} index={0}>
                <ProductLinesSelector
                  permissions={permissions}
                  selectedIndustry={selectedIndustry}
                  config={config}
                />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                <BrandsSelector
                  permissions={permissions}
                  selectedIndustry={selectedIndustry}
                  config={config}
                />
              </CustomTabPanel>
            </Grid2>
            <AutoSaveMessage permission={permissions?.industryId} />
          </Grid2>
        </Grid2>
      )}
      {view === null && <SkeletonListBoxSelector height="400px" />}
    </PageSection>
  );
}

export default SkillsSetup;
